/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ApiError, http, setUnauthorizedHandler } from '@/shared/lib/api';
import { clearLocalDrafts } from '@/shared/lib/clear-local-drafts';
import { posthog } from '@/shared/lib/posthog';
import type {
  AuthStatus,
  LoginResponse,
  ManagerTenantOption,
  Role,
  User,
} from '@/shared/lib/auth/types';

interface UserRaw {
  id: string;
  email: string;
  name?: string | null;
  role: Role;
  is_owner?: boolean;
  isOwner?: boolean;
  mfa_enabled?: boolean;
  mfaEnabled?: boolean;
  mfa_activated_at?: string | null;
  mfaActivatedAt?: string | null;
  tenant_id?: string | null;
  tenantId?: string | null;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    setup_wizard_completed: boolean;
    trial_locked_at: string | null;
    demo_ends_at: string | null;
    is_demo: boolean;
  } | null;
  impersonation?: { actor_admin_id: string } | null;
  manager_context?: {
    tenant_id: string;
    tenant_name: string;
    available_tenants: ManagerTenantOption[];
  } | null;
  simple_mode?: boolean;
  simpleMode?: boolean;
  default_collection_favorite_id?: string | null;
  defaultCollectionFavoriteId?: string | null;
}

interface MeResponseRaw {
  success: true;
  user: UserRaw;
}

type LoginStage =
  | 'mfa_required'
  | 'mfa_enrollment_required'
  | 'password_change_required'
  | 'manager_tenant_select_required'
  | 'authenticated';

interface LoginResponseRaw {
  stage: LoginStage;
  mfa_token?: string;
  user?: UserRaw;
}

type EnrollResponseRaw =
  | { stage: 'authenticated'; user: UserRaw }
  | { stage: 'manager_tenant_select_required' };

const AUTH_QUERY_KEY = ['auth', 'me'] as const;

// Bootstraps `/api/auth/me` across a fully offline cold start (mobile role,
// 30-day session — see CLAUDE.md). Without this, a fresh app launch with no
// connectivity at all has no in-memory query data to fall back on, and the
// isError branch below parks the whole app on the loading skeleton forever
// (there is never a successful fetch to escape it). Storing the last-known
// user gives `initialData` something to render immediately while the real
// fetch retries in the background; `initialDataUpdatedAt` uses the real
// cache time so react-query still treats it as stale and refetches on mount.
const AUTH_SNAPSHOT_KEY = 'vanagreen:auth-snapshot';

function readAuthSnapshot(): { user: User; cachedAt: number } | null {
  try {
    const raw = localStorage.getItem(AUTH_SNAPSHOT_KEY);
    return raw ? (JSON.parse(raw) as { user: User; cachedAt: number }) : null;
  } catch {
    return null;
  }
}

function writeAuthSnapshot(user: User): void {
  try {
    localStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify({ user, cachedAt: Date.now() }));
  } catch {
    /* best effort */
  }
}

function clearAuthSnapshot(): void {
  try {
    localStorage.removeItem(AUTH_SNAPSHOT_KEY);
  } catch {
    /* best effort */
  }
}

function normalizeUser(raw: UserRaw): User {
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name ?? null,
    role: raw.role,
    isOwner: raw.isOwner ?? raw.is_owner ?? false,
    mfaEnabled: raw.mfaEnabled ?? raw.mfa_enabled ?? false,
    mfaActivatedAt: raw.mfaActivatedAt ?? raw.mfa_activated_at ?? null,
    tenantId: raw.tenantId ?? raw.tenant_id ?? null,
    tenant: raw.tenant
      ? {
          id: raw.tenant.id,
          name: raw.tenant.name,
          slug: raw.tenant.slug,
          setupWizardCompleted: raw.tenant.setup_wizard_completed,
          trialLockedAt: raw.tenant.trial_locked_at,
          demoEndsAt: raw.tenant.demo_ends_at,
          isDemo: raw.tenant.is_demo,
        }
      : undefined,
    impersonation: raw.impersonation
      ? { actorAdminId: raw.impersonation.actor_admin_id }
      : null,
    managerContext: raw.manager_context
      ? {
          tenantId: raw.manager_context.tenant_id,
          tenantName: raw.manager_context.tenant_name,
          availableTenants: raw.manager_context.available_tenants,
        }
      : null,
    simpleMode: raw.simpleMode ?? raw.simple_mode ?? false,
    defaultCollectionFavoriteId:
      raw.defaultCollectionFavoriteId ?? raw.default_collection_favorite_id ?? null,
  };
}

interface ManagerTenantsResponseRaw {
  success: true;
  data: ManagerTenantOption[];
}

interface ManagerSelectResponseRaw {
  stage: 'authenticated';
  user: UserRaw;
}

export interface VerifyMfaResult {
  stage: 'authenticated' | 'manager_tenant_select_required';
}

export interface EnrollMfaResult {
  stage: 'authenticated' | 'manager_tenant_select_required';
}

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  mfaToken: string | null;
  sessionExpired: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<LoginResponse>;
  verifyMfa: (code: string, rememberDevice?: boolean) => Promise<VerifyMfaResult>;
  enrollMfa: (code: string) => Promise<EnrollMfaResult>;
  logout: () => Promise<void>;
  refetchMe: () => Promise<void>;
  /** PLAN.md item 7 — manager multi-tenant switching. */
  listManagerTenants: () => Promise<ManagerTenantOption[]>;
  selectManagerTenant: (tenantId: string) => Promise<void>;
  switchManagerTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaStage, setMfaStage] = useState<
    'mfa_required' | 'mfa_enrollment' | 'manager_tenant_select' | null
  >(null);
  const [forcedStatus, setForcedStatus] = useState<AuthStatus | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;
  const tRef = useRef(t);
  tRef.current = t;

  const [authSnapshot] = useState(() => readAuthSnapshot());

  const meQuery = useQuery<User | null, ApiError>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        const raw = await http.get<MeResponseRaw>('/api/auth/me');
        const user = normalizeUser(raw.user);
        writeAuthSnapshot(user);
        return user;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.code === 'SESSION_EXPIRED')) {
          clearAuthSnapshot();
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
    initialData: authSnapshot?.user,
    initialDataUpdatedAt: authSnapshot?.cachedAt,
  });

  // 401 / SESSION_EXPIRED interceptor — installed once.
  useEffect(() => {
    setUnauthorizedHandler((err) => {
      // Ignore /api/auth/me's own 401 (handled in queryFn).
      if (err.code === 'SESSION_EXPIRED' || err.status === 401) {
        const wasAuthenticated = queryClient.getQueryData<User | null>(AUTH_QUERY_KEY);
        clearAuthSnapshot();
        queryClient.setQueryData(AUTH_QUERY_KEY, null);
        setMfaToken(null);
        setMfaStage(null);
        setForcedStatus('unauthenticated');
        if (wasAuthenticated) {
          const isPublic = ['/login', '/mfa', '/mobile-only'].some((p) =>
            locationRef.current.pathname.startsWith(p),
          );
          if (!isPublic) {
            setSessionExpired(true);
          }
        }
      }
    });
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [queryClient]);

  // PostHog identity — never sends email/name, only the opaque user/tenant
  // ids already used everywhere else (JWT, DB). Keyed on meQuery.data (not
  // just logout()) so it also covers session expiry and manager tenant
  // switches, which set the same query data without calling logout().
  useEffect(() => {
    const user = meQuery.data;
    if (user) {
      posthog.identify(user.id, { role: user.role, tenant_id: user.tenantId });
    } else if (user === null) {
      posthog.reset();
    }
  }, [meQuery.data]);

  const status: AuthStatus = useMemo(() => {
    if (forcedStatus === 'unauthenticated') return 'unauthenticated';
    if (mfaStage === 'mfa_required') return 'mfa_challenge';
    if (mfaStage === 'mfa_enrollment') return 'mfa_enrollment';
    if (mfaStage === 'manager_tenant_select') return 'manager_tenant_select';
    if (meQuery.isPending) return 'loading';
    // Network/server error (non-auth) with no identity to fall back on at
    // all (no in-memory data, no persisted snapshot): keep loading rather
    // than force the user back to the login page. When there IS a — possibly
    // stale — user available (in-memory from this session, or the persisted
    // snapshot from a prior one), prefer showing the app over an infinite
    // skeleton; every real request still re-validates against the session
    // cookie server-side regardless of this client-side status.
    if (meQuery.isError && !meQuery.data) return 'loading';
    return meQuery.data ? 'authenticated' : 'unauthenticated';
  }, [forcedStatus, mfaStage, meQuery.isPending, meQuery.isError, meQuery.data]);

  const transitionToAuthenticated = useCallback(
    async (rawUser?: UserRaw): Promise<User | null> => {
      const user = rawUser ? normalizeUser(rawUser) : null;
      setMfaToken(null);
      setMfaStage(null);
      setForcedStatus(null);
      setSessionExpired(false);
      if (user) {
        // `rawUser` is already the authoritative, just-minted snapshot from
        // the response body — setQueryData alone is enough to notify every
        // subscriber. Also invalidating+refetching here was a real bug
        // (2026-07-30): a manager switching tenants repeatedly could have
        // an EARLIER switch's own `/me` refetch (still in flight, sent
        // under that switch's now-superseded session cookie) resolve
        // AFTER a LATER switch already set the correct data, silently
        // clobbering it back to the wrong tenant — the "takes doing it
        // twice" symptom. Only refetch when we truly have no user object
        // to set directly (verifyMfa's 'authenticated' stage below).
        writeAuthSnapshot(user);
        queryClient.setQueryData(AUTH_QUERY_KEY, user);
        return user;
      }
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      await queryClient.refetchQueries({ queryKey: AUTH_QUERY_KEY });
      return user;
    },
    [queryClient],
  );

  const login = useCallback(
    async (
      email: string,
      password: string,
      turnstileToken?: string,
    ): Promise<LoginResponse> => {
      // „Ține-mă minte” is asked on the code screen (verifyMfa below): trusting
      // the device skips the code, not the password.
      const raw = await http.post<LoginResponseRaw>('/api/auth/login', {
        email,
        password,
        turnstile_token: turnstileToken,
      });
      if (raw.stage === 'mfa_required') {
        // mfa_token cookie is HttpOnly; backend may also echo a token in body for
        // backward compatibility — pass it to verifyMfa body if present.
        setMfaToken(raw.mfa_token ?? null);
        setMfaStage('mfa_required');
        setForcedStatus(null);
        return { stage: 'mfa_required', mfa_token: raw.mfa_token ?? null };
      }
      if (raw.stage === 'mfa_enrollment_required') {
        setMfaToken(null);
        setMfaStage('mfa_enrollment');
        setForcedStatus(null);
        return { stage: 'mfa_enrollment_required' };
      }
      if (raw.stage === 'password_change_required') {
        // Force-change token is set as an HttpOnly cookie by the backend.
        // We just navigate the user to the change-password page.
        setForcedStatus(null);
        return { stage: 'password_change_required' };
      }
      if (raw.stage === 'manager_tenant_select_required') {
        // PLAN.md item 7 — devMfaBypass/trusted-device paths can reach
        // "authenticated" without an MFA challenge in between; a manager
        // still needs to pick a tenant before any session exists.
        setMfaToken(null);
        setMfaStage('manager_tenant_select');
        setForcedStatus(null);
        return { stage: 'manager_tenant_select_required' };
      }
      const user = await transitionToAuthenticated(raw.user);
      return { stage: 'authenticated', user: user as User };
    },
    [transitionToAuthenticated],
  );

  const verifyMfa = useCallback(
    async (code: string, rememberDevice?: boolean): Promise<VerifyMfaResult> => {
      if (mfaStage !== 'mfa_required') {
        throw new ApiError({
          code: 'AUTH_MFA_EXPIRED',
          message: 'MFA token missing',
          status: 401,
        });
      }
      // mfa_token is also carried via HttpOnly cookie; pass body field only when present.
      const body: Record<string, unknown> = { code, remember_device: rememberDevice ?? false };
      if (mfaToken) body.mfa_token = mfaToken;
      const raw = await http.post<{ stage: 'authenticated' | 'manager_tenant_select_required' }>(
        '/api/auth/mfa/verify',
        body,
      );
      if (raw.stage === 'manager_tenant_select_required') {
        setMfaToken(null);
        setMfaStage('manager_tenant_select');
        setForcedStatus(null);
        return { stage: 'manager_tenant_select_required' };
      }
      await transitionToAuthenticated();
      return { stage: 'authenticated' };
    },
    [mfaStage, mfaToken, transitionToAuthenticated],
  );

  const enrollMfa = useCallback(
    async (code: string): Promise<EnrollMfaResult> => {
      if (mfaStage !== 'mfa_enrollment') {
        throw new ApiError({
          code: 'AUTH_MFA_EXPIRED',
          message: 'MFA enrollment session missing',
          status: 401,
        });
      }
      const raw = await http.post<EnrollResponseRaw>('/api/auth/mfa/enable', { code });
      if (raw.stage === 'manager_tenant_select_required') {
        setMfaToken(null);
        setMfaStage('manager_tenant_select');
        setForcedStatus(null);
        return { stage: 'manager_tenant_select_required' };
      }
      await transitionToAuthenticated(raw.user);
      return { stage: 'authenticated' };
    },
    [mfaStage, transitionToAuthenticated],
  );

  const listManagerTenants = useCallback(async (): Promise<ManagerTenantOption[]> => {
    const raw = await http.get<ManagerTenantsResponseRaw>('/api/auth/manager/tenants');
    return raw.data;
  }, []);

  const selectManagerTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      const raw = await http.post<ManagerSelectResponseRaw>('/api/auth/manager/select-tenant', {
        tenant_id: tenantId,
      });
      await transitionToAuthenticated(raw.user);
      // Same reasoning as switchManagerTenant below — logout() already
      // clears these, but a session that expired without an explicit
      // logout (tab closed, token just aged out) can leave a stale draft
      // behind for whoever logs in next on this browser.
      clearLocalDrafts();
    },
    [transitionToAuthenticated],
  );

  const switchManagerTenant = useCallback(
    async (tenantId: string): Promise<void> => {
      const raw = await http.post<ManagerSelectResponseRaw>('/api/auth/manager/switch-tenant', {
        tenant_id: tenantId,
      });
      await transitionToAuthenticated(raw.user);
      // Every other cached query (collections, companies, dashboard, ...)
      // belongs to the PREVIOUS tenant's data. Unlike a fresh login, this is
      // a live in-place switch on an already-populated cache — nothing else
      // invalidates on its own, so every screen kept showing stale data
      // until a manual page refresh.
      //
      // `removeQueries` (tried first) only deletes the cache entries — it
      // does NOT make an already-mounted component (e.g. the collections
      // list page the manager is currently looking at) refetch immediately,
      // so the visible page kept showing the old tenant's data until a
      // manual reload even though the sidebar/tenant name updated fine.
      // `invalidateQueries` with `refetchType: 'active'` is what actually
      // forces every currently-active query observer to refetch right now.
      //
      // `cancelQueries` first closes a narrower race: any request for the
      // PREVIOUS tenant that was already in flight the instant the switch
      // happened (sent under the old session cookie, correctly scoped to
      // the old tenant server-side) must not be allowed to land in the
      // cache afterwards — otherwise its now-stale response could briefly
      // overwrite the fresh refetch below with the wrong tenant's data.
      // Cancelling aborts it before that can happen.
      await queryClient.cancelQueries({
        predicate: (query) => query.queryKey[0] !== AUTH_QUERY_KEY[0],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== AUTH_QUERY_KEY[0],
        refetchType: 'active',
      });
      clearLocalDrafts();
    },
    [transitionToAuthenticated, queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await http.post('/api/auth/logout');
    } finally {
      // Wipe the mobile field app's offline store so no PII (cached sources,
      // queued sheets) survives logout. Best-effort; named by convention to
      // avoid coupling shared/auth to the mobile module.
      try {
        indexedDB.deleteDatabase('recyclers-mobile');
      } catch {
        /* best effort — eviction also happens on session expiry */
      }
      clearAuthSnapshot();
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
      // A different tenant's user can log in next on this same browser —
      // a leftover draft (e.g. collections' localStorage new-draft, saved
      // under this tenant's flow types) must not silently resurface and
      // submit a cross-tenant reference under the next login.
      clearLocalDrafts();
      setMfaToken(null);
      setMfaStage(null);
      setForcedStatus('unauthenticated');
      navigateRef.current('/login', { replace: true });
    }
  }, [queryClient]);

  const refetchMe = useCallback(async (): Promise<void> => {
    setForcedStatus(null);
    await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    await queryClient.refetchQueries({ queryKey: AUTH_QUERY_KEY });
  }, [queryClient]);

  // Reset forcedStatus once a fresh authenticated user lands.
  useEffect(() => {
    if (meQuery.data) {
      setForcedStatus(null);
    }
  }, [meQuery.data]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      status,
      mfaToken,
      sessionExpired,
      login,
      verifyMfa,
      enrollMfa,
      logout,
      refetchMe,
      listManagerTenants,
      selectManagerTenant,
      switchManagerTenant,
    }),
    [
      meQuery.data,
      status,
      mfaToken,
      sessionExpired,
      login,
      verifyMfa,
      enrollMfa,
      logout,
      refetchMe,
      listManagerTenants,
      selectManagerTenant,
      switchManagerTenant,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
